package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Grid cell for the All-Categories screen: an elevated, rounded card with a circular icon badge
 * and CENTERED name + count beneath. All copy comes from the caller (localized).
 */
@Composable
fun CategoryGridCard(
    name: String,
    /** Localized "n businesses" line; null hides it (an empty category shows no discouraging zero). */
    countLabel: String?,
    iconName: String,
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    // Illustrated category image (see [categoryImageRes]); when set it replaces the glyph icon
    // and the badge circle goes neutral so the artwork's own colors carry the card.
    imageRes: org.jetbrains.compose.resources.DrawableResource? = null,
) {
    AppCard(
        modifier = modifier.fillMaxWidth(),
        onClick = onClick,
        elevation = AppTheme.elevation.level2,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(AppTheme.spacing.lg),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            Box(
                modifier = Modifier
                    .size(AppTheme.sizes.categoryBadge)
                    .background(
                        if (imageRes != null) AppTheme.colors.surfaceContainerHigh
                        else AppTheme.colors.primaryContainer,
                        AppTheme.shapes.pill,
                    ),
                contentAlignment = Alignment.Center,
            ) {
                if (imageRes != null) {
                    androidx.compose.foundation.Image(
                        painter = org.jetbrains.compose.resources.painterResource(imageRes),
                        contentDescription = null,
                        modifier = Modifier.size(AppTheme.sizes.categoryTile),
                    )
                } else {
                    AppIcon(
                        name = iconName,
                        size = AppTheme.sizes.iconMd,
                        tint = AppTheme.colors.onPrimaryContainer,
                    )
                }
            }
            // Exactly two lines are always reserved (minLines = maxLines = 2) so every card in
            // the grid has the SAME height regardless of how long the category name is.
            Text(
                text = name,
                style = AppTheme.typography.labelMd,
                color = AppTheme.colors.onSurface,
                textAlign = TextAlign.Center,
                minLines = 2,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.fillMaxWidth(),
            )
            if (countLabel != null) Text(
                text = countLabel,
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant,
                textAlign = TextAlign.Center,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}
